import React from 'react';
import type { ComponentType } from 'react';
import { DELIVERABLE_REGISTRY } from '@discovr/types';
import {
  Check,
  Plus,
  CaretDown,
  ChartLineUp,
  Briefcase,
  Monitor,
  Leaf,
  Heartbeat,
  GraduationCap,
  Flower,
  ForkKnife,
  AirplaneTilt,
  Tag,
  Image as ImageIcon,
  type Icon,
  type IconWeight,
} from '../../design-system/icons';
import {
  YoutubeShortsIcon,
  YoutubePlayIcon,
  InstagramStoryIcon,
  InstagramReelIcon,
  InstagramPostIcon,
  InstagramCarouselIcon,
  InstagramCollabReelIcon,
} from '../../components/icons';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { Input } from '../../components/ui/input';
import { PRIMARY_GOAL_OPTIONS } from './editFieldOptions';
import { primaryGoalMeta } from './editFieldMeta';

/**
 * Rich pickers for the three campaign fields whose options carry their own
 * iconography in Figma — primary goal, content types and creator categories.
 *
 * The meta below mirrors apps/brand/.../NewCampaign (DeliverablesSection's
 * TYPE_META, StepTargeting's CATEGORY_ICONS, constants' PRIMARY_GOALS), kept as
 * a local copy for the same reason editFieldOptions.ts is: packages/ui can't
 * import from an app. The glyphs themselves are already shared — they live in
 * packages/ui/src/components/icons.
 */

// Broader than Phosphor's `Icon` so this slot also accepts the hand-authored
// Figma glyphs, which are plain function components.
type Glyph = ComponentType<{ size?: number; weight?: IconWeight; className?: string }>;

// Instagram brand gradient (the brand mark, NOT the Discovr AI gradient) — an
// approved exception. YouTube uses brand red.
const ICON_BOX_BG: Record<string, string> = {
  instagram: 'linear-gradient(135deg, #FEDA75 0%, #FA7E1E 25%, #D62976 50%, #962FBF 75%, #4F5BD5 100%)',
  youtube: '#FF0000',
};

const TYPE_META: Record<string, { Icon: Glyph; sub: string }> = {
  instagram_stories: { Icon: InstagramStoryIcon, sub: 'Instagram · 24h' },
  instagram_static_post: { Icon: InstagramPostIcon, sub: 'Instagram · Feed' },
  instagram_carousel: { Icon: InstagramCarouselIcon, sub: 'Instagram · Multi' },
  instagram_non_collab_reel: { Icon: InstagramReelIcon, sub: 'Instagram · Video' },
  instagram_collab_reel: { Icon: InstagramCollabReelIcon, sub: 'Instagram · 2 creators' },
  youtube_integrated: { Icon: YoutubePlayIcon, sub: 'YouTube · Sponsors' },
  youtube_dedicated: { Icon: YoutubePlayIcon, sub: 'YouTube · Long-form' },
  youtube_shorts: { Icon: YoutubeShortsIcon, sub: 'YouTube · Vertical' },
};

// Card order per platform, to match Figma. Registry order untouched.
const MAIN_ORDER: Record<string, string[]> = {
  instagram: [
    'instagram_stories',
    'instagram_non_collab_reel',
    'instagram_static_post',
    'instagram_carousel',
    'instagram_collab_reel',
  ],
  youtube: ['youtube_integrated', 'youtube_dedicated', 'youtube_shorts'],
};

const CATEGORY_ICONS: Record<string, Icon> = {
  finance: ChartLineUp,
  business: Briefcase,
  tech: Monitor,
  lifestyle: Leaf,
  health: Heartbeat,
  education: GraduationCap,
  beauty: Flower,
  food: ForkKnife,
  travel: AirplaneTilt,
};

/** Filled brand check — the selected marker across all three pickers. */
function PurpleCheck({ size = 18 }: { size?: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-(--brand-500) text-white"
      style={{ width: size, height: size }}
    >
      <Check size={size - 6} weight="bold" />
    </span>
  );
}

// ── Primary goal ──────────────────────────────────────────────────────────

/**
 * Single-select menu (Figma): each row is its goal's icon + label, and the
 * chosen one is highlighted with a plain check on the right — not the brand
 * check, which Figma reserves for the card/tile pickers below.
 */
export function PrimaryGoalSelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const current = primaryGoalMeta(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 text-left shadow-xs transition-colors hover:border-foreground/30 focus-visible:border-foreground focus-visible:ring-[3px] focus-visible:ring-foreground/15 focus-visible:outline-none"
        >
          <span className="flex min-w-0 items-center gap-2">
            <current.Icon size={16} className="shrink-0 text-muted-foreground" />
            <span className="truncate text-body-sm-medium text-foreground">
              {current.label || 'Select a goal'}
            </span>
          </span>
          <CaretDown size={16} className="shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-(--radix-popover-trigger-width) min-w-56 p-1">
        {PRIMARY_GOAL_OPTIONS.map((o) => {
          const { Icon: GoalIcon } = primaryGoalMeta(o.value);
          const selected = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-body-sm-regular text-foreground transition-colors ${
                selected ? 'bg-muted' : 'hover:bg-muted'
              }`}
            >
              <GoalIcon size={16} className="shrink-0 text-foreground" />
              <span className="min-w-0 flex-1 truncate">{o.label}</span>
              {selected && <Check size={14} weight="bold" className="shrink-0 text-foreground" />}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

// ── Content types ─────────────────────────────────────────────────────────

/**
 * Stacked Figma cards: gradient platform tile + label + sublabel, brand check
 * on the right when selected. Figma: 191×58 cards, 12px insets, 8px apart —
 * ChipMultiSelect's menu is sized to w-[215px] p-3 to land the card at 191.
 */
export function ContentTypeCards({
  platform,
  values,
  onChange,
}: {
  platform: string;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const order = MAIN_ORDER[platform] ?? [];
  const rank = (type: string) => {
    const i = order.indexOf(type);
    return i === -1 ? order.length : i;
  };
  const types = Object.entries(DELIVERABLE_REGISTRY)
    .filter(([, meta]) => meta.category === 'main' && (meta.platform === platform || meta.platform === '*'))
    .sort(([a], [b]) => rank(a) - rank(b));

  const toggle = (type: string) =>
    onChange(values.includes(type) ? values.filter((v) => v !== type) : [...values, type]);

  return (
    <div className="flex flex-col gap-2">
      {types.map(([type, meta]) => {
        const Glyph = TYPE_META[type]?.Icon ?? ImageIcon;
        const selected = values.includes(type);
        return (
          <button
            key={type}
            type="button"
            onClick={() => toggle(type)}
            className={`flex h-[58px] items-center gap-3 rounded-xl border-[1.5px] bg-card px-3 text-left transition-colors ${
              selected ? 'border-foreground' : 'border-border hover:border-foreground/30'
            }`}
          >
            <span
              className="flex size-9 shrink-0 items-center justify-center rounded-[10px] text-white"
              style={{ background: ICON_BOX_BG[platform] ?? 'var(--muted)' }}
            >
              <Glyph size={18} weight="regular" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-body-sm-medium text-foreground">{meta.label}</span>
              <span className="truncate text-caption-regular text-muted-foreground">
                {TYPE_META[type]?.sub ?? platform}
              </span>
            </span>
            {selected && <PurpleCheck />}
          </button>
        );
      })}
    </div>
  );
}

// ── Creator categories ────────────────────────────────────────────────────

/**
 * Two-column Figma tiles: muted icon box + label, brand check on the right when
 * selected. Figma: 168×56 tiles, 8px apart — ChipMultiSelect's menu is sized to
 * w-[368px] p-3 to land the columns at 168. The last tile opens a free-text
 * input for a category outside the preset list.
 */
export function CategoryTiles({
  options,
  values,
  onChange,
}: {
  options: string[];
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  // Custom categories aren't in the preset list but must still show as tiles.
  const all = [...options, ...values.filter((v) => !options.includes(v))];

  const toggle = (cat: string) =>
    onChange(values.includes(cat) ? values.filter((v) => v !== cat) : [...values, cat]);

  const commit = () => {
    const cat = draft.trim().toLowerCase();
    if (cat && !values.includes(cat)) onChange([...values, cat]);
    setDraft('');
    setAdding(false);
  };

  return (
    <div className="grid grid-cols-2 gap-2">
      {all.map((cat) => {
        const CatIcon = CATEGORY_ICONS[cat] ?? Tag;
        const selected = values.includes(cat);
        return (
          <button
            key={cat}
            type="button"
            onClick={() => toggle(cat)}
            className={`flex h-14 items-center gap-3 rounded-xl border-[1.5px] bg-card px-3 text-left transition-colors ${
              selected ? 'border-foreground' : 'border-border hover:border-foreground/30'
            }`}
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
              <CatIcon size={16} />
            </span>
            <span className="min-w-0 flex-1 truncate text-body-sm-medium text-foreground capitalize">{cat}</span>
            {selected && <PurpleCheck />}
          </button>
        );
      })}
      {adding ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') {
              setDraft('');
              setAdding(false);
            }
          }}
          placeholder="Category name"
          className="h-14 rounded-xl"
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex h-14 items-center justify-center gap-2 rounded-xl border-[1.5px] border-border bg-card text-body-sm-medium text-foreground transition-colors hover:border-foreground/30"
        >
          Add category
          <Plus size={16} weight="bold" />
        </button>
      )}
    </div>
  );
}
